import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router';
import { ThemeProvider } from 'styled-components';
import { HelmetProvider } from 'react-helmet-async';
import { theme } from './theme';
import { GlobalStyle } from './globalStyles';
import { AuthProvider } from './components/auth/AuthContext';
import { I18nProvider } from './i18n/I18nContext';
import { AdminMessageProvider } from './contexts/AdminMessageContext';
import { CartProvider } from './contexts/CartContext';
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import CookieConsent from './components/layout/CookieConsent';

const Home = lazy(() => import('./pages/Home'));
const Catalog = lazy(() => import('./pages/Catalog'));
const DiscoverCreators = lazy(() => import('./pages/DiscoverCreators'));
const Login = lazy(() => import('./pages/Login'));
const AddonLogin = lazy(() => import('./pages/AddonLogin'));
const LoginCallback = lazy(() => import('./pages/LoginCallback'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ProductDetail = lazy(() => import('./components/marketplace/ProductDetail'));
const PublicUserProfile = lazy(() => import('./components/marketplace/PublicUserProfile'));
const EnhancedAnalyticsDashboard = lazy(() => import('./components/admin/EnhancedAnalyticsDashboard'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const PrivacySettings = lazy(() => import('./pages/PrivacySettings'));
const UnsubscribeCreatorMarketing = lazy(() => import('./pages/UnsubscribeCreatorMarketing'));
const About = lazy(() => import('./pages/About'));
const TermsOfUse = lazy(() => import('./pages/TermsOfUse'));
const CheckoutSuccess = lazy(() => import('./pages/CheckoutSuccess'));
const CheckoutCancel = lazy(() => import('./pages/CheckoutCancel'));
const TwoFactorLogin = lazy(() => import('./pages/TwoFactorLogin'));
const TwoFactorSetup = lazy(() => import('./pages/TwoFactorSetup'));
const Guide = lazy(() => import('./pages/Guide'));
const UserGuide = lazy(() => import('./pages/UserGuide'));
const CreatorGuide = lazy(() => import('./pages/CreatorGuide'));
const Feed = lazy(() => import('./pages/Feed'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const Imprint = lazy(() => import('./pages/Imprint'));
const CopyrightPolicy = lazy(() => import('./pages/CopyrightPolicy'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const Accessibility = lazy(() => import('./pages/Accessibility'));
const Report = lazy(() => import('./pages/Report'));
const RenderLab = lazy(() => import('./pages/RenderLab'));
const OfficialAddons = lazy(() => import('./pages/OfficialAddons'));
const EmbedProductViewer = lazy(() => import('./pages/EmbedProductViewer'));
const PublicCollection = lazy(() => import('./pages/PublicCollection'));
const ProjectBriefs = lazy(() => import('./pages/ProjectBriefs'));
const ProjectBriefDetail = lazy(() => import('./pages/ProjectBriefDetail'));
const CreateProjectBrief = lazy(() => import('./pages/CreateProjectBrief'));
const ChallengeDetail = lazy(() => import('./pages/ChallengeDetail'));
const NotFound = lazy(() => import('./pages/NotFound'));

const PageFallback = () => <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;

const App = () => {
  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <I18nProvider>
        <AuthProvider>
          <CartProvider>
            <AdminMessageProvider>
              <Router>
                <Suspense fallback={<PageFallback />}>
                  <Routes>
                    <Route path="/embed/product/:id" element={<EmbedProductViewer />} />
                    <Route path="/*" element={
                      <Layout>
                        <CookieConsent />
                        <Routes>
                          <Route path="/" element={<Home />} />
                          <Route path="/catalog" element={<Catalog />} />
                          <Route path="/briefs" element={<ProjectBriefs />} />
                          <Route path="/briefs/new" element={<ProtectedRoute><CreateProjectBrief /></ProtectedRoute>} />
                          <Route path="/briefs/:id" element={<ProjectBriefDetail />} />
                          <Route path="/challenges/:slug" element={<ChallengeDetail />} />
                          <Route path="/creators" element={<DiscoverCreators />} />
                          <Route path="/feed" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
                          <Route path="/render-lab" element={<RenderLab />} />
                          <Route path="/addons" element={<OfficialAddons />} />
                          <Route path="/addons/:platform" element={<OfficialAddons />} />
                          <Route path="/login" element={<Login />} />
                          <Route path="/addon-login" element={<AddonLogin />} />
                          <Route path="/login/2fa" element={<TwoFactorLogin />} />
                          <Route path="/login_callback" element={<LoginCallback />} />
                          <Route path="/settings/2fa" element={<ProtectedRoute><TwoFactorSetup /></ProtectedRoute>} />
                          <Route path="/register" element={<Register />} />
                          <Route path="/forgot-password" element={<ForgotPassword />} />
                          <Route path="/reset-password" element={<ResetPassword />} />
                          <Route path="/product/:id" element={<ProductDetail />} />
                          <Route path="/user/:userId" element={<PublicUserProfile />} />
                          <Route path="/storefront/:username/:slug" element={<PublicCollection />} />
                          <Route path="/analytics" element={<ProtectedRoute requireAdmin><EnhancedAnalyticsDashboard /></ProtectedRoute>} />
                          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                          <Route path="/about" element={<About />} />
                          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                          <Route path="/privacy-settings" element={<ProtectedRoute><PrivacySettings /></ProtectedRoute>} />
                          <Route path="/unsubscribe/creator-marketing" element={<UnsubscribeCreatorMarketing />} />
                          <Route path="/terms-of-use" element={<TermsOfUse />} />
                          <Route path="/checkout/success" element={<CheckoutSuccess />} />
                          <Route path="/checkout/cancel" element={<CheckoutCancel />} />
                          <Route path="/guide" element={<Guide />} />
                          <Route path="/guide/users" element={<UserGuide />} />
                          <Route path="/guide/creators" element={<CreatorGuide />} />
                          <Route path="/cookie-policy" element={<CookiePolicy />} />
                          <Route path="/imprint" element={<Imprint />} />
                          <Route path="/copyright-policy" element={<CopyrightPolicy />} />
                          <Route path="/refund-policy" element={<RefundPolicy />} />
                          <Route path="/accessibility" element={<Accessibility />} />
                          <Route path="/report" element={<Report />} />
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </Layout>
                    } />
                  </Routes>
                </Suspense>
              </Router>
            </AdminMessageProvider>
          </CartProvider>
        </AuthProvider>
        </I18nProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
};

export default App;
